import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { catchError, map, Observable, of } from "rxjs";
import { IntegrationStore } from "src/app/features/common-data/stores/integration.store";
import { VendorStore } from "src/app/features/common-data/stores/vendor.store";
import { TransferMediumStore } from "src/app/features/vendor/stores/transfer-medium.store";
import { Integration } from "src/app/shared/models/integration.model";
import { Vendor } from "src/app/shared/models/vendor.model";


export interface AdminIntegrationRouteData {
    client: { id: string; name: string }
    vendor: { id: string; name: string, url?: string };
}

@Injectable({ providedIn: 'root' })
export class AdminIntegrationGuard implements CanActivate {
    constructor(
        private integrationStore: IntegrationStore,
        private vendorStore: VendorStore,
        private transferMediumStore: TransferMediumStore,
        private router: Router
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        const integrationIdParam = route.paramMap.get('integrationId');
        const vendorIdParam = route.paramMap.get('id');

        if (!integrationIdParam || !vendorIdParam) {
            console.error('ID Parameter sind erforderlich');
            return false;
        }

        const integrationId = +integrationIdParam;
        const vendorId = +vendorIdParam;

        // Lade TransferMediums (kann parallel laufen)
        this.transferMediumStore.loadTransferMediums();

        // Strategie 1: Prüfe Navigation State
        const navigationState = this.router.getCurrentNavigation()?.extras?.state;
        if (navigationState?.['integrationData']) {
            route.data = {
                ...route.data,
                integrationData: navigationState['integrationData']
            };
            console.log('Daten aus Navigation State geladen');
            return true;
        }

        // Strategie 2: Lade aus Store oder Server (für bestehende Integration)
        if (integrationId > 0) {
            let integration = this.integrationStore.getIntegrationById(integrationId);

            if (integration) {
                // Integration im Store vorhanden
                this.setIntegrationData(route, integration, vendorId, integrationId);
                console.log('Daten aus Store geladen');
                return true;
            } else {
                // Integration muss vom Server geladen werden - WARTEN!
                console.log('Lade Integration vom Server...');
                return this.integrationStore.loadIntegrationById(integrationId).pipe(
                    map((integration) => {
                        this.setIntegrationData(route, integration, vendorId, integrationId);
                        console.log('Daten vom Server geladen');
                        return true;
                    }),
                    catchError((error) => {
                        console.error('Fehler beim Laden der Integration:', error);
                        // Fallback auf "Unbekannt"
                        this.setFallbackData(route, vendorId, integrationId);
                        return of(true);
                    })
                );
            }
        }

        // Strategie 3: Neue Integration (ID = -1)
        const vendor = this.vendorStore.getVendorByIdFromSubjects(vendorId);
        this.setFallbackData(route, vendorId, integrationId, vendor);
        console.log('Fallback-Daten für neue Integration verwendet');
        return true;
    }

    private setIntegrationData(
        route: ActivatedRouteSnapshot,
        integration: Integration,
        vendorId: number,
        integrationId: number
    ) {
        const integrationData: AdminIntegrationRouteData
         = {
            vendor: {
                id: integration.vendor?.id?.toString() || vendorId.toString(),
                name: integration.vendor?.name || 'Unbekannter Lieferant',
                url: `/vendors/${integration.vendor?.id?.toString() || vendorId}/update`
            },
            client: {
                id: integration.clientId?.toString() || '',
                name: integration.clientName || 'Unbekannter Kunde'
            }
        };

        route.data = {
            ...route.data,
            integrationData
        };
    }

    private setFallbackData(
        route: ActivatedRouteSnapshot,
        vendorId: number,
        integrationId: number,
        vendor?: Vendor
    ) {
        const integrationData: AdminIntegrationRouteData = {
            vendor: {
                id: vendorId.toString(),
                name: vendor?.name || 'Unbekannter Lieferant',
                url: `/vendors/${vendorId}/update`
            },
            client: {
                id: '',
                name: integrationId === -1 ? 'Neue Integration' : 'Unbekannter Kunde'
            }
        };

        route.data = {
            ...route.data,
            integrationData
        };
    }
}